"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Play, Loader2, ArrowLeft, Upload, Search, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../shared/ReviewComment";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface DepreciationIntangibleAssetsProps {
  onBack?: () => void;
}

export default function DepreciationIntangibleAssets({ onBack }: DepreciationIntangibleAssetsProps) {
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [depreciationMethod, setDepreciationMethod] = useState<"wdvm" | "sl">("wdvm");
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<{
    textResponse?: string;
    documents?: any[];
  }>({});
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `DEPR_INTANGIBLE_${normalizedSection}`;
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Depreciation on Tangible/Intangible Assets", "Depreciation on Intangible Assets");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question data
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const fileName = 'Execution_PPE_Responses.json';
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data) {
          const substantiveProcedures = result.data.substantiveProcedures || {};
          const questionResponse = substantiveProcedures['DEPRECIATION-INTANGIBLE-ASSETS-INFERENCE'];
          if (questionResponse) {
            setInferenceQuestion({
              textResponse: questionResponse.textResponse?.plainText || questionResponse.textResponse?.html?.replace(/<[^>]*>/g, '') || '',
              documents: questionResponse.documents || []
            });
          }
        }
      } catch (error) {
        console.error('Failed to load inference question:', error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger search on mount
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    console.log(" Starting file search for Depreciation on Intangible Assets...");
    
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_IT_IntAR");
        console.log(" SharePoint API result:", result);
        
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          
          if (files.length > 0) {
            console.log(" Files found:", files);
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            console.log(" No files found");
            setFileSearchStatus("not_found");
          }
        } else {
          console.log(" API call failed:", result.error);
          setFileSearchStatus("not_found");
        }
      } else {
        console.error("SharePoint API not available");
        setFileSearchStatus("not_found");
      }
    } catch (e) {
      console.error("Error searching for Depreciation files:", e);
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_IT_IntAR"]
              });

              if (result.success) {
                console.log(" File uploaded successfully:", file.name);
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                console.error(" Upload failed:", result.error);
                setUploadStatus("error");
              }
            } else {
              console.error("SharePoint API not available");
              setUploadStatus("error");
            }
          } catch (error) {
            console.error("Error uploading file:", error);
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch (error) {
      console.error("Error in uploadFile:", error);
      setUploadStatus("error");
    }
  };

  const runProcedure = async () => {
    if (!selectedFile) return;
    
    // Save file name and depreciation method to file usage reference
    const entries: FileUsageEntry[] = [];
    const fileName = selectedFile.includes(' (') ? selectedFile.split(' (')[0] : selectedFile;
    entries.push({ "Search for files": fileName });
    entries.push({ "Depreciation Method": depreciationMethod });
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Depreciation on Tangible/Intangible Assets", "Depreciation on Intangible Assets", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");

    try {
      const scriptKey = depreciationMethod === "wdvm" ? "execute_depr_intangible_wdvm" : "execute_depr_intangible_sl";
      
      if (window.payroll?.run) {
        const result = await window.payroll.run(scriptKey, {
          inputFiles: [],
          options: {
            start_date: startDate || undefined,
            end_date: endDate || undefined
          }
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      console.error("Error running procedure:", e);
      setProcessingStatus("error");
    }
  };

  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Get text from localStorage if available (DocumentManager auto-saves)
      const itemId = 'DEPRECIATION-INTANGIBLE-ASSETS-INFERENCE';
      const localStorageKey = `autoSaveTextbox_${itemId}`;
      const savedText = localStorage.getItem(localStorageKey) || inferenceQuestion.textResponse || '';

      const fileName = 'Execution_PPE_Responses.json';

      // Download existing file
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn('Failed to download existing file, starting fresh:', error);
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Wait a bit for localStorage sync
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Query db.json for linked/uploaded documents
      const section = `Execution_PPE_DEPRECIATION-INTANGIBLE-ASSETS-INFERENCE`;
      let documents: any[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const docResult = await (window as any).sharePointAPI.loadDocumentListBySection(section);
          if (docResult?.success && Array.isArray(docResult.data)) {
            documents = docResult.data;
          }
        }
      } catch (error) {
        console.warn('Failed to load documents from db.json:', error);
      }

      // Update inference question data
      existingData.substantiveProcedures[itemId] = {
        selectedOption: 'textbox',
        textResponse: {
          html: savedText,
          plainText: savedText
        },
        documents: documents,
        lastUpdated: new Date().toISOString(),
        companyName: companyName
      };

      // Upload updated file
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution PPE Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_PPE_Responses`],
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save inference question:', error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Depreciation on Intangible Assets</h2>
          <p className="text-gray-400">Select file and run depreciation recomputation</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for files"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search for files"])?.["Search for files"]}
                  </p>
                )}
              </div>
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Select Date Range</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Select Date Range"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-black/40 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Select Depreciation Method</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Select Depreciation Method"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup value={depreciationMethod} onValueChange={(value) => setDepreciationMethod(value as "wdvm" | "sl")}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="wdvm" id="wdvm" />
              <Label htmlFor="wdvm" className="text-white cursor-pointer">Written Down Value method</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sl" id="sl" />
              <Label htmlFor="sl" className="text-white cursor-pointer">Straight-line method</Label>
            </div>
          </RadioGroup>
          {fileUsageReference.find(e => e["Depreciation Method"]) && (
            <p className="text-xs text-white/60 mt-1">
              Last used: {fileUsageReference.find(e => e["Depreciation Method"])?.["Depreciation Method"] === "wdvm" ? "Written Down Value method" : fileUsageReference.find(e => e["Depreciation Method"])?.["Depreciation Method"] === "sl" ? "Straight-line method" : fileUsageReference.find(e => e["Depreciation Method"])?.["Depreciation Method"]}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Run Procedure
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run Procedure"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={runProcedure} 
              disabled={!selectedFile || processingStatus === "running"} 
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Run Procedure"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="text-green-400">Successful</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400">Failed</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="ppe"
            itemId="DEPRECIATION-INTANGIBLE-ASSETS-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Select Date Range")]: "Select Date Range",
              [getSectionQuestionId("Select Depreciation Method")]: "Select Depreciation Method",
              [getSectionQuestionId("Run Procedure")]: "Run Procedure",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

